import { getApiClient, type ApiFileUploadResult } from '@littlebird/sdk';

interface UploadTextFileOptions {
  extension?: string;
  contentType?: string;
  onProgress?: (progress: { loaded: number; total: number }) => void;
}

interface FeedbackAttachmentUploadResult {
  url: string;
  s3_key: string;
}

async function putFile(
  uploadUrl: string,
  file: File,
  onProgress?: (progress: { loaded: number; total: number }) => void
): Promise<void> {
  const { promise } = getApiClient().uploadFileWithProgress(
    uploadUrl,
    file,
    onProgress || (() => {})
  );

  const uploadResponse = await promise;

  if (!uploadResponse.ok) {
    throw new Error(`Failed to upload file: ${uploadResponse.statusText}`);
  }
}

export async function uploadFileToS3(
  file: File,
  onProgress?: (progress: { loaded: number; total: number }) => void
): Promise<string> {
  const { url: uploadUrl, download_key: downloadKey } =
    await getApiClient().fetch<ApiFileUploadResult>('get_logs_upload_url', {
      method: 'POST',
      body: JSON.stringify({ filename: file.name }),
    });

  if (!downloadKey) throw new Error('No download key received');

  await putFile(uploadUrl, file, onProgress);

  return `${import.meta.env.VITE_API_URL}/log/${downloadKey}`;
}

/**
 * Uploads a private feedback attachment and returns its S3 key for staff.
 * Unlike `uploadFileToS3`, the result is not reachable through the public `/log` route
 */
export async function uploadFeedbackAttachmentToS3(
  file: File
): Promise<string> {
  const { url: uploadUrl, s3_key: s3Key } =
    await getApiClient().fetch<FeedbackAttachmentUploadResult>(
      'get_feedback_attachment_upload_url',
      {
        method: 'POST',
        body: JSON.stringify({ filename: file.name }),
      }
    );

  if (!s3Key) throw new Error('No S3 key received');

  await putFile(uploadUrl, file);

  return s3Key;
}

export async function uploadTextFileToS3(
  content: string,
  fileName: string,
  options: UploadTextFileOptions = {}
): Promise<string> {
  const { extension = 'txt', contentType = 'text/plain', onProgress } = options;

  const textFile = new File([content], `${fileName}.${extension}`, {
    type: contentType,
  });

  return uploadFileToS3(textFile, onProgress);
}

/**
 * Uploads log content to S3 and returns the download URL
 * @param logContent The log file content as string
 * @param fileName The log file name
 * @param onProgress Optional progress callback
 * @returns The download URL of the uploaded log
 */
export async function uploadLogToS3(
  logContent: string,
  fileName: string,
  onProgress?: (progress: { loaded: number; total: number }) => void
): Promise<string> {
  return uploadTextFileToS3(logContent, fileName, {
    extension: 'txt',
    contentType: 'text/plain',
    onProgress,
  });
}
